import React, { useState, useEffect, useRef } from 'react';
import {
  School,
  Target,
  TrendingUp,
  Shield,
  BarChart3,
  Brain,
  CheckCircle,
  AlertCircle,
  ChevronRight,
  MapPin,
  Users,
  DollarSign,
  Calendar,
  Sparkles,
  Building,
  Award
} from 'lucide-react';

const CollegeSelectionDemo = ({ data }) => {
  const [selectedCollege, setSelectedCollege] = useState(null);
  const [showStrategy, setShowStrategy] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isInView) {
          setIsInView(true);
          setTimeout(() => setSelectedCollege(data.colleges[0]), 1000);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isInView, data.colleges]);

  const getTierColor = (tier) => {
    switch(tier) {
      case 'reach': return 'text-violet-600 bg-violet-50 border-violet-200';
      case 'target': return 'text-azure-600 bg-azure-50 border-azure-200';
      case 'safety': return 'text-emerald-600 bg-emerald-50 border-emerald-200';
      default: return 'text-slate-600 bg-slate-50 border-slate-200';
    }
  };

  const getTierIcon = (tier) => {
    switch(tier) {
      case 'reach': return <TrendingUp className="w-4 h-4" />;
      case 'target': return <Target className="w-4 h-4" />;
      case 'safety': return <Shield className="w-4 h-4" />;
      default: return null;
    }
  };

  return (
    <div ref={sectionRef} className="max-w-6xl mx-auto px-4">
      {/* Section Header */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-50 rounded-full mb-4">
          <School className="w-4 h-4 text-emerald-600" />
          <span className="text-sm font-medium text-emerald-700">
            Strategic College Selection
          </span>
        </div>
        <h2 className="text-4xl font-bold text-slate-900 mb-4">
          Data-Driven College List Building
        </h2>
        <p className="text-xl text-slate-600 max-w-3xl mx-auto">
          We analyze academic profile, program strengths, campus culture, and outcomes data
          to build a balanced list that maximizes your opportunities while aligning with your goals.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Left: College List */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-lg border border-slate-200">
            {/* List Header */}
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900 text-lg">
                  Sheppard's Strategic College List
                </h3>
                <div className="flex items-center gap-2 text-sm">
                  <BarChart3 className="w-4 h-4 text-slate-500" />
                  <span className="text-slate-600">Balance Score: </span>
                  <span className="font-bold text-emerald-600">Optimal</span>
                </div>
              </div>

              {/* Distribution Bar */}
              <div className="flex gap-1 h-2">
                <div className="bg-violet-500 rounded-l" style={{ width: '40%' }} />
                <div className="bg-azure-500" style={{ width: '40%' }} />
                <div className="bg-emerald-500 rounded-r" style={{ width: '20%' }} />
              </div>
              <div className="flex justify-between text-xs text-slate-600 mt-2">
                <span>2 Reach (40%)</span>
                <span>2 Target (40%)</span>
                <span>1 Safety (20%)</span>
              </div>
            </div>

            {/* College Cards */}
            <div className="p-6 space-y-4">
              {data.colleges.map((college, index) => (
                <div
                  key={college.id}
                  className={`border rounded-lg p-4 cursor-pointer transition-all
                    ${selectedCollege?.id === college.id
                      ? 'ring-2 ring-azure-500 bg-azure-50/50 border-azure-300'
                      : 'hover:shadow-md border-slate-200'}`}
                  onClick={() => {
                    setSelectedCollege(college);
                    setShowStrategy(true);
                  }}
                  style={{
                    animationDelay: `${index * 100}ms`,
                    animation: isInView ? 'fadeInSlide 0.6s ease-out forwards' : 'none'
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-semibold text-slate-900">{college.name}</h4>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium border flex items-center gap-1
                          ${getTierColor(college.tier)}`}>
                          {getTierIcon(college.tier)}
                          {college.tier}
                        </span>
                        {college.deadline && (
                          <span className="text-xs text-slate-500 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(college.deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        )}
                      </div>

                      <div className="text-sm text-slate-600 mb-3">
                        {college.whyFit}
                      </div>

                      {/* Fit Indicators */}
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1">
                          <div className="flex items-center">
                            <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-gradient-to-r from-azure-500 to-azure-600"
                                style={{ width: `${college.fitScore}%` }}
                              />
                            </div>
                          </div>
                          <span className="text-xs font-medium text-slate-700 ml-2">
                            {college.fitScore}% match
                          </span>
                        </div>

                        {college.status === 'drafting' && (
                          <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded font-medium">
                            In Progress
                          </span>
                        )}
                        {college.status === 'ready' && (
                          <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded font-medium flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            Ready
                          </span>
                        )}
                      </div>
                    </div>

                    <ChevronRight className={`w-5 h-5 transition-transform
                      ${selectedCollege?.id === college.id ? 'rotate-90 text-azure-600' : 'text-slate-400'}`} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: Strategy Panel */}
        <div className="space-y-4">
          {/* Selected College Details */}
          {selectedCollege && showStrategy && (
            <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6"
              style={{
                animation: 'fadeInScale 0.4s ease-out forwards'
              }}
            >
              <div className="flex items-center gap-2 mb-4">
                <Brain className="w-5 h-5 text-violet-600" />
                <h3 className="font-semibold text-slate-900">
                  Application Strategy
                </h3>
              </div>

              <div className="space-y-4">
                <div>
                  <div className="text-sm font-medium text-slate-700 mb-1">
                    {selectedCollege.name} Focus
                  </div>
                  <div className="text-sm text-slate-600 p-3 bg-violet-50 rounded-lg border border-violet-200">
                    {selectedCollege.strategy}
                  </div>
                </div>

                {selectedCollege.essays && (
                  <div>
                    <div className="text-sm font-medium text-slate-700 mb-2">Essay Requirements</div>
                    <div className="space-y-2">
                      <div className="text-sm text-slate-600 p-2 bg-slate-50 rounded">
                        <span className="font-medium">Main:</span> {selectedCollege.essays.main}
                      </div>
                      {selectedCollege.essays.supplemental?.length > 0 && (
                        <div className="text-sm text-slate-500">
                          + {selectedCollege.essays.supplemental.length} supplemental essays
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Why This List Works */}
          <div className="bg-gradient-to-br from-azure-50 to-indigo-50 rounded-xl border border-azure-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-azure-600" />
              <h3 className="font-semibold text-slate-900">Strategic Insights</h3>
            </div>

            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <Building className="w-4 h-4 text-azure-500 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <div className="font-medium text-slate-900">Program Alignment</div>
                  <div className="text-slate-600">All schools have top-tier civil engineering programs with urban planning connections</div>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <Users className="w-4 h-4 text-azure-500 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <div className="font-medium text-slate-900">Culture Fit</div>
                  <div className="text-slate-600">Mix of technical rigor and interdisciplinary opportunities matches your interests</div>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <Award className="w-4 h-4 text-azure-500 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <div className="font-medium text-slate-900">Competitive Edge</div>
                  <div className="text-slate-600">Your city planning experience is exceptionally rare for engineering applicants</div>
                </div>
              </div>
            </div>
          </div>

          {/* Risk Analysis */}
          <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
            <h3 className="font-semibold text-slate-900 mb-3">List Analysis</h3>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Application Load</span>
                <div className="flex items-center gap-2">
                  <div className="w-20 h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div className="h-full bg-azure-500" style={{ width: '60%' }} />
                  </div>
                  <span className="text-sm font-medium text-slate-900">Moderate</span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Financial Safety</span>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span className="text-sm font-medium text-emerald-600">Covered</span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Geographic Diversity</span>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-azure-500" />
                  <span className="text-sm font-medium text-slate-900">3 regions</span>
                </div>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-slate-200">
              <div className="text-xs text-emerald-600 font-medium">
                ✓ Well-balanced list with clear safety options
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeInSlide {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
};

export default CollegeSelectionDemo;